import React, { useState, useEffect } from 'react';
import { parseApiUtc, parseApiDateOnly } from '@/utils/apiDate';
import { appDate } from '@/utils/appDateFormat';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/ui/AppIcon';
import { LeaveRequestView, LeaveRequestStatus, LeaveRequestAttachmentView } from '../../../../models/attendance';
import * as LeavesApi from '../../../../api/Attendance/leaves';
import { leaveStatusLabel } from '../../../../utils/leaveStatus';
import { getApiErrorMessage } from '../../../../utils/apiErrors';

interface LeaveDetailsModalProps {
  record: LeaveRequestView;
  onClose: () => void;
  onCancel?: (record: LeaveRequestView) => void;
}

const LeaveDetailsModal: React.FC<LeaveDetailsModalProps> = ({ record, onClose, onCancel }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const [attachments, setAttachments] = useState<LeaveRequestAttachmentView[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  // CO-575 w2 — the refusal surface for the Download button, stated inside the Attachments block.
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  useEffect(() => {
    if (record.attachmentCount > 0) {
      loadAttachments();
    }
  }, [record.id]);

  const loadAttachments = async () => {
    setLoadingAttachments(true);
    try {
      const result = await LeavesApi.getLeaveRequestAttachments(record.id);
      if (result.errors.length === 0) {
        setAttachments(result.payload);
      }
    } catch {
      // silently fail
    }
    setLoadingAttachments(false);
  };

  const handleDownloadAttachment = async (attachment: LeaveRequestAttachmentView) => {
    setAttachmentError(null);
    try {
      const blob = await LeavesApi.previewLeaveAttachment(attachment.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      // CO-575 w2: "silently fail" on a user-initiated download. Clicking Download produced no file
      // and no explanation — the employee could not tell a refusal from a browser that had blocked
      // the save. The interceptor parses a JSON error Blob back into the `Result` shape, so the
      // server's reason is available right here.
      setAttachmentError(getApiErrorMessage(err, t, t('attachmentDownloadError')));
    }
  };

  const statusColors: Record<string, string> = {
    [LeaveRequestStatus.Approved]: 'bg-green-100 text-green-800',
    [LeaveRequestStatus.Rejected]: 'bg-red-100 text-red-800',
    [LeaveRequestStatus.Pending]: 'bg-orange-100 text-orange-800',
    [LeaveRequestStatus.Cancelled]: 'bg-gray-100 text-gray-800',
    [LeaveRequestStatus.CancellationPending]: 'bg-amber-100 text-amber-800',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="leave-details-modal">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-foreground">{t('Leave Request Details')}</h3>
            <button
              onClick={onClose}
              data-testid="leave-details-close"
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {/* Request Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Leave Type')}</label>
              <p className="text-foreground font-medium">{record.leaveType}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Status')}</label>
              <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusColors[record.status]}`}>
                {leaveStatusLabel(t, record.status)}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Start Date')}</label>
              <p className="text-foreground font-medium">
                {appDate(parseApiDateOnly(record.startDate))}
                {record.fromHalfDay && ` (${t('Half day')} - ${record.fromHalfDayPart})`}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('End Date')}</label>
              <p className="text-foreground font-medium">
                {appDate(parseApiDateOnly(record.endDate))}
                {record.toHalfDay && ` (${t('Half day')} - ${record.toHalfDayPart})`}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Duration')}</label>
              <p className="text-foreground font-medium">
                {record.numberOfDays} {record.numberOfDays === 1 ? t('day') : t('days')}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Applied Date')}</label>
              <p className="text-foreground font-medium">{appDate(parseApiUtc(record.dateApplied))}</p>
            </div>
          </div>

          {/* Reason */}
          {record.comments && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Reason')}</label>
              <p className="text-foreground mt-1">{record.comments}</p>
            </div>
          )}

          {/* Reviewers */}
          {record.reviewers && record.reviewers.length > 0 ? (
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Reviewers')}</label>
              <div className="mt-2 space-y-3">
                {record.reviewers.map((reviewer, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                    <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${
                      reviewer.status === 'Approved' ? 'bg-green-500' :
                      reviewer.status === 'Rejected' ? 'bg-red-500' : 'bg-orange-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{reviewer.reviewerName}</span>
                        <span className="text-xs text-muted-foreground">
                          {reviewer.actionDate && appDate(parseApiUtc(reviewer.actionDate))}
                        </span>
                      </div>
                      <span className={`text-xs font-medium ${
                        reviewer.status === 'Approved' ? 'text-green-600' :
                        reviewer.status === 'Rejected' ? 'text-red-600' : 'text-orange-600'
                      }`}>
                        {reviewer.status && t(reviewer.status)}
                      </span>
                      {reviewer.comments && (
                        <p className="text-sm text-muted-foreground mt-1">{reviewer.comments}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Fallback: flat manager info for non-workflow requests */}
              {(record.decidedByName || record.managerName) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">{t('Reviewed by')}</label>
                    <p className="text-foreground font-medium">{record.decidedByName || record.managerName}</p>
                  </div>
                </div>
              )}
              {record.managerComments && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('Reviewer Comments')}</label>
                  <p className="text-foreground mt-1">{record.managerComments}</p>
                </div>
              )}
            </>
          )}

          {/* Attachments */}
          {record.attachmentCount > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('Attachments')}</label>
              {loadingAttachments ? (
                <p className="text-sm text-muted-foreground mt-2">{t('Loading')}...</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center space-x-2 rtl:space-x-reverse p-2 bg-muted/30 rounded-lg">
                      <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{attachment.name}</span>
                      <button
                        onClick={() => handleDownloadAttachment(attachment)}
                        data-testid="leave-details-attachment-download"
                        className="text-blue-600 hover:text-blue-700 text-sm ml-auto"
                      >
                        {t('Download')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* CO-575 w2 — why the download did not happen. */}
              {attachmentError && (
                <div
                  className="mt-2 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                  role="alert"
                  data-testid="leave-details-attachment-error"
                >
                  <Icon name="AlertTriangle" size={16} className="shrink-0" />
                  <span>{attachmentError}</span>
                </div>
              )}
            </div>
          )}

          {/* Cancel button for pending or approved requests */}
          {(record.status === LeaveRequestStatus.Pending || record.status === LeaveRequestStatus.Approved) && onCancel && (
            <div className="pt-4 border-t border-border">
              <button
                onClick={() => onCancel(record)}
                data-testid="leave-details-cancel-btn"
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 rounded-lg transition-colors"
              >
                <Icon name="XCircle" size={16} className="inline ltr:mr-2 rtl:ml-2" />
                {record.status === LeaveRequestStatus.Approved ? t('Request Cancellation') : t('Cancel Request')}
              </button>
              {record.status === LeaveRequestStatus.Approved && (
                <p className="text-xs text-muted-foreground mt-2">{t('cancellationRequiresApproval')}</p>
              )}
            </div>
          )}

          {/* Cancellation pending info */}
          {record.status === LeaveRequestStatus.CancellationPending && (
            <div className="pt-4 border-t border-border">
              <div className="flex items-center space-x-2 rtl:space-x-reverse p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Icon name="Clock" size={16} className="text-amber-600" />
                <span className="text-sm text-amber-800">{t('Cancellation Pending Approval')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveDetailsModal;
